<script>

import MembershipSystemForm from '@/views/AadenPages/memberShipReborn/Core/MembershipSystemForm.vue'
import PromotionalContent from '@/views/AadenPages/memberShipReborn/Core/PromotionalContent.vue'
import MembershipSystemPreview from '@/views/AadenPages/memberShipReborn/Core/MembershipSystemPreview.vue'
import { getMembershipSystem, updateMembershipSystem } from '@/model/dataLayer/api/MembershipSystem/MembershipSystem'
import { getJoinedBrandName } from '@/model/dataLayer/api/Vip/BusinessLayer'
import GlobalConfig from '@/model/global/GlobalSettings'
import { showSuccessMessage } from '@/model/utils/utils'
import LoadingProvider from '@/views/widget/LoadingProvider.vue'
import JoinedBrandNotice from '@/views/AadenPages/memberShipReborn/Common/JoinedBrandNotice.vue'

export default {
  name: 'SystemIndex',
  components: {
    JoinedBrandNotice,
    LoadingProvider,
    MembershipSystemForm,
    PromotionalContent,
    MembershipSystemPreview
  },
  data () {
    return {
      tab: 0,
      loading: false,
      systemInfo: {},
      previewDialog: false,
      // 门店没有自己的会员体系时：已加入品牌 → 品牌名；独立门店 → ''
      noSystem: false,
      brandName: ''
    }
  },
  async mounted () {
    await this.reload()
  },
  computed: {
    displayInfo: function () {
      return this.systemInfo.displayInfo
    },
    textColor: function () {
      if (!this.systemInfo.displayInfo || !this.systemInfo.displayInfo.color) {
        return 'black'
      }
      return this.calculateTextColor(this.systemInfo.displayInfo.color)
    }
  },
  methods: {
    handleTabChange (val) {
      this.tab = val
    },
    async reload () {
      this.loading = true
      const system = await getMembershipSystem()

      // 一店一个会员体系：门店加入品牌后自己的体系停用，云端这里返回空，由品牌在会员后台统一管理
      if (!system?.id) {
        this.noSystem = true
        this.brandName = await getJoinedBrandName(GlobalConfig.blId)
        this.loading = false
        return
      }
      this.systemInfo = system

      // Ensure displayInfo exists
      if (!this.systemInfo.displayInfo) {
        this.systemInfo.displayInfo = {
          name: '',
          description: '',
          imageUrl: '',
          color: '#eeeeee'
        }
      }

      this.loading = false
    },

    // Calculate appropriate text color (black or white) based on background color
    calculateTextColor (bgColor) {
      // Default to black if no color is provided
      if (!bgColor) return 'black'

      // Calculate luminance
      let r, g, b

      // Remove # if present
      bgColor = bgColor.replace('#', '')

      // Handle shorthand hex (#RGB)
      if (bgColor.length === 3) {
        r = parseInt(bgColor[0] + bgColor[0], 16) / 255
        g = parseInt(bgColor[1] + bgColor[1], 16) / 255
        b = parseInt(bgColor[2] + bgColor[2], 16) / 255
      } else {
        r = parseInt(bgColor.substring(0, 2), 16) / 255
        g = parseInt(bgColor.substring(2, 4), 16) / 255
        b = parseInt(bgColor.substring(4, 6), 16) / 255
      }

      // Calculate luminance using WCAG formula
      const luminance = 0.2126 * r + 0.7152 * g + 0.0722 * b

      // Use white text for dark backgrounds, black text for light backgrounds
      return luminance > 0.5 ? 'black' : 'white'
    },
    async updateSystem (updatedInfo) {
      this.systemInfo = updatedInfo
      await this.saveSystem()
    },
    async saveSystem () {
      await updateMembershipSystem(this.systemInfo)
      showSuccessMessage('成功了！')
    },

    // Method to refresh the membership levels in the preview
    refreshMembershipLevelsPreview () {
      if (this.$refs.preview) {
        this.$refs.preview.loadMembershipLevels()
      }
    }
  }
}
</script>

<template>
  <div>
    <loading-provider :loading="loading">
      <joined-brand-notice
        v-if="noSystem && brandName"
        :brand-name="brandName"
      >
        会员体系、等级、集点卡、资产和营销规则由品牌在会员后台统一管理，本店原有的会员体系已停用，会员已转入品牌。
        收银机上加会员、查会员、集点和储值照常使用。
      </joined-brand-notice>
      <v-card
        v-else-if="noSystem"
        elevation="2"
        class="pa-6 text-body-2"
        max-width="640"
      >
        本店还没有会员体系。在收银机上加入第一位会员时会自动创建。
      </v-card>
      <v-row v-else>
        <!-- Left side: Membership System Preview -->
        <v-col
          cols="12"
          md="4"
          class=""
        >
          <membership-system-preview
            ref="preview"
            :system-info="systemInfo"
            :text-color="textColor"
          />
        </v-col>

        <!-- Right side: Tabbed Settings -->
        <v-col
          cols="12"
          md="8"
        >
          <v-card elevation="2">
            <v-tabs
              v-model="tab"
              background-color="transparent"
              color="primary"
              touchless
              show-arrows
              next-icon="mdi-arrow-right"
              prev-icon="mdi-arrow-left"
              center-active
              fixed-tabs
              active-class="active-tab"
              @change="handleTabChange"
              @click.prevent
            >
              <v-tab
                :value="0"
                @click.native.stop.prevent="handleTabChange(0)"
              >
                基本信息
              </v-tab>
              <v-tab
                :value="1"
                @click.native.stop.prevent="handleTabChange(1)"
              >
                详细介绍
              </v-tab>
            </v-tabs>
            <v-divider />
            <v-card-text class="pa-0">
              <v-tabs-items
                v-model="tab"
                style="background: transparent"
                eager
                @change="handleTabChange"
              >
                <v-tab-item :value="0">
                  <membership-system-form
                    :system-info="systemInfo"
                    :text-color="textColor"
                    @update:system-info="updateSystem"
                  />
                </v-tab-item>
                <v-tab-item :value="1">
                  <promotional-content
                    :system-info="systemInfo"
                    @update:system-info="updateSystem"
                  />
                </v-tab-item>
              </v-tabs-items>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </loading-provider>
  </div>
</template>

<style scoped>
.active-tab {
  font-weight: bold;
  color: #1976d2 !important;
}
</style>
